import { ActionCreator, bindActionCreators } from "redux";
import { Dispatch } from "redux";
import { BatchAction, batchActions } from "redux-batched-actions";
import { ThunkAction } from "redux-thunk";
import { ICanvasEditorState } from "../../widgets/interfaces/model/CanvasEditorState";
import { PageObjectType } from "../../widgets/interfaces/model/Types";
import { getSelectedPageObjects } from "../../widgets/selectors/selection";
import {
	ClipboardActionConstants,
	ICopyObjectsAction,
	IDeleteObjectsAction,
	IDeselectAllObjectAction,
	IResetClipboardAction,
	IUpdateClipboardAction,
	PageActionConstants,
	SelectionConstants,
} from "../reducers/index";

/**
 * Copy object action creator.
 * @returns copy action.
 */
export const copy: ActionCreator<ThunkAction<ICopyObjectsAction, ICanvasEditorState, void, ICopyObjectsAction>> = () =>
	(dispatch: Dispatch<ICopyObjectsAction>, getState: () => ICanvasEditorState): ICopyObjectsAction => {
		const state: ICanvasEditorState = getState();
		const { selectedPageObjects, selectedPage } = state.selection.present;
		const { pasteObjectOffset } = state.canvasEditor;
		const clearOnPaste: boolean = false;
		// Throw error message if there is no page and object selected
		if (!selectedPageObjects.length || !selectedPage) {
			throw "ERROR: Page and at least one object should be selected to perform copy action.";
		}
		const selectedObjects: PageObjectType[] = getSelectedPageObjects(state) as PageObjectType[];
		return dispatch({
			transient: true,
			type: ClipboardActionConstants.COPY,
			selectedObjects,
			clearOnPaste,
			pasteObjectOffset,
		});
	};

/**
 * Reset clipboard action creator.
 * @returns reset action.
 */
export const resetClipboard: ActionCreator<IResetClipboardAction> = (): IResetClipboardAction => ({
	transient: true,
	type: ClipboardActionConstants.RESET_CLIPBOARD,
});

/**
 * Cut object action creator.
 * @returns cut action.
 */
export const cut: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> = () =>
	(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		const state: ICanvasEditorState = getState();
		const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
		const { pasteObjectOffset } = state.canvasEditor;
		const clearOnPaste: boolean = true;
		// Throw error messgae if there is no page and object selected
		if (!selectedPageObjects.length || !selectedPage) {
			throw "ERROR: Page and at least one object should be selected to perform cut action.";
		}
		if (groupSelectionIndices && groupSelectionIndices.length > 0) {
			throw "ERROR: Cannot cut within groups.";
		}

		// get selected page objects
		const selectedObjects: PageObjectType[] = getSelectedPageObjects(state) as PageObjectType[];

		// copy objects action
		const copyObjectAction: ICopyObjectsAction = {
			transient: true,
			type: ClipboardActionConstants.COPY,
			selectedObjects,
			clearOnPaste,
			pasteObjectOffset,
		};

		// delete objects action
		const deleteObjectsAction: IDeleteObjectsAction = {
			groupSelectionIndices,
			selectedPage,
			selectedPageObjects,
			type: PageActionConstants.DELETE_OBJECTS,
		};

		// deselect all objects
		const deselectAllObjects: IDeselectAllObjectAction = {
			transient: true,
			type: SelectionConstants.DESELECT_ALL_OBJECTS,
		};
		return dispatch(batchActions([copyObjectAction, deleteObjectsAction, deselectAllObjects]));
	};

/**
 * Update clipboard action creator.
 * Update copied objects offset by 20
 * so that on next paste it will get paste relative to last paste objects.
 * @returns update action.
 */
export const updateClipboard: ActionCreator<ThunkAction<IUpdateClipboardAction, ICanvasEditorState, void, IUpdateClipboardAction>> =
	() => (dispatch: Dispatch<IUpdateClipboardAction>, getState: () => ICanvasEditorState): IUpdateClipboardAction => {
		const state: ICanvasEditorState = getState();
		const { pasteObjectOffset } = state.canvasEditor;
		return dispatch({
			transient: true,
			type: ClipboardActionConstants.UPDATE_CLIPBOARD,
			pasteObjectOffset,
		});
	};

/**
 * Canvas Api
 * Collection of redux dispatch bound actions
 */
export const initClipboardAPI = (dispatch: Dispatch<any>) => {
	return bindActionCreators({ copy, updateClipboard, cut, resetClipboard }, dispatch);
};
